<template>
    <div class="test-decision-element">
        <div class="test-element">
            <DsCheckbox
                v-if="isCheckbox"
                v-model="selectedInternalFormCheckbox"
                :label="label"
                @input="onCheckboxInput"
            />

            <DsDatePicker
                v-if="isDatePicker"
                v-model="selectedDatePicker"
                date-only
                :label="label"
                class="ds-datepicker-wrapper"
                @input="onDatePickerInput"
            />

            <DsInputField
                v-if="isInputTextField"
                v-model="selectedInputTextField"
                class="ds-input-field-wrapper"
                :placeholder="label"
                @blur="onBlurSaveInputTextField"
            />

            <DsMultiselect
                v-if="isMultiselectInputField && !isMultipleInput"
                v-model="selectedMultiselectInputField"
                :placeholder="label"
                class="ds-multiselect-wrapper"
                :options="multiselectInputFieldOptions"
                @input="onMultiselectInput"
            />

            <DsMultiselect
                v-else-if="isMultipleInput"
                v-model="selectedInputAutocomplete"
                multiple
                searchable
                :placeholder="label"
                class="ds-multiselect-wrapper"
                :options="autocompleteOptions"
                @input="onAutoCompleteInput"
            />

            <div v-if="isSelectRadio">
                <span>{{ label }}</span>
                <DsRadioGroup
                    v-model="selectedRadio"
                    :options="radioOptions"
                    @input="toggleRadioOption"
                />
            </div>

            <div v-if="isYesNoRadio">
                <span>{{ label }}</span>
                <DsRadio
                    v-model="radioYesNoValue"
                    val="1"
                    :label="$t('funnel.edit.decision.evaluate.yes.label')"
                    @input="toggleYesNoRadio"
                />
                <DsRadio
                    v-model="radioYesNoValue"
                    val="0"
                    :label="$t('funnel.edit.decision.evaluate.no.label')"
                    @input="toggleYesNoRadio"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {
    getTestInputType,
    getTestInputAttributes,
    getLabel,
    getAllUsers,
} from './decision.api';
import {
    convertFromDpToString,
    convertFromStringToDp,
    INPUT_TYPE_AUTOCOMPLETE,
    INPUT_TYPE_SELECT_CHOSEN,
    INPUT_TYPE_DATE,
    INPUT_TYPE_CITY,
    INPUT_TYPE_STRING,
    INPUT_TYPE_NAME,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_PHONE,
    INPUT_TYPE_STATE,
    INPUT_TYPE_URL,
    INPUT_TYPE_COUNTRY,
    INPUT_TYPE_EMAIL_STATUS,
    INPUT_TYPE_SELECT_LANGUAGE_CODE,
    INPUT_TYPE_LEADSOURCE,
    INPUT_TYPE_CONTACT_TYPE,
    INPUT_TYPE_CONTACT_TITLE,
    INPUT_TYPE_USERS,
    INPUT_TYPE_CURRENCY,
    INPUT_SELECT_WITH_DEFAULT,
    INPUT_DOUBLE,
    INPUT_INTEGER,
    INPUT_PERCENT,
    INPUT_DRILL_DOWN,
    INPUT_DAY_OF_WEEK,
    INPUT_SELECT_RADIO,
    INPUT_YES_NO_RADIO,
    INPUT_STATE_SELECT,
    INPUT_TEXT_AREA,
    INPUT_YEAR,
    INPUT_CUSTOM_EMAIL,
    INPUT_DATETIME,
    INPUT_MULTISELECT,
    INPUT_MONTH,
    INPUT_BOOLEAN,
} from './decisionDiamond.util';

export default {
    props: {
        ruleField: {
            type: Object,
            required: true,
        },
        ddKeys: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
    },
    emits: ['update-value'],
    data() {
        return {
            label: '',
            inputType: null,
            isMultipleInput: false,
            selectedInputAutocomplete: [],
            selectedRadio: '',
            radioOptions: [],
            radioYesNoValue: '',
            selectedDatePicker: '',
            selectedInputTextField: '',
            selectedInternalFormCheckbox: false,
            selectedMultiselectInputField: null,
            autocompleteOptions: [],
            multiselectInputFieldOptions: [],
        };
    },
    computed: {
        fieldId() {
            return `${this.ruleField.subject}_${this.ruleField.category}_${this.ruleField.field}`;
        },
        fieldKey() {
            return this.ruleField.field;
        },
        isYesNoRadio() {
            return this.inputType === INPUT_YES_NO_RADIO;
        },
        isCheckbox() {
            return this.inputType === INPUT_BOOLEAN;
        },
        isSelectRadio() {
            return this.inputType === INPUT_SELECT_RADIO;
        },
        isDatePicker() {
            return this.inputType === INPUT_TYPE_DATE
                || this.inputType === INPUT_DATETIME;
        },
        isInputTextField() {
            return this.inputType === INPUT_TYPE_CITY
                || this.inputType === INPUT_TYPE_STRING
                || this.inputType === INPUT_TYPE_NAME
                || this.inputType === INPUT_TYPE_EMAIL
                || this.inputType === INPUT_TYPE_PHONE
                || this.inputType === INPUT_TYPE_STATE
                || this.inputType === INPUT_TYPE_URL
                || this.inputType === INPUT_TYPE_CURRENCY
                || this.inputType === INPUT_DOUBLE
                || this.inputType === INPUT_INTEGER
                || this.inputType === INPUT_PERCENT
                || this.inputType === INPUT_TEXT_AREA
                || this.inputType === INPUT_YEAR
                || this.inputType === INPUT_CUSTOM_EMAIL;
        },
        isMultiselectInputField() {
            return this.inputType === INPUT_TYPE_COUNTRY
                || this.inputType === INPUT_TYPE_EMAIL_STATUS
                || this.inputType === INPUT_TYPE_SELECT_LANGUAGE_CODE
                || this.inputType === INPUT_TYPE_LEADSOURCE
                || this.inputType === INPUT_TYPE_CONTACT_TYPE
                || this.inputType === INPUT_TYPE_CONTACT_TITLE
                || this.inputType === INPUT_TYPE_USERS
                || this.inputType === INPUT_SELECT_WITH_DEFAULT
                || this.inputType === INPUT_DRILL_DOWN
                || this.inputType === INPUT_DAY_OF_WEEK
                || this.inputType === INPUT_STATE_SELECT
                || this.inputType === INPUT_MONTH
                || this.inputType === INPUT_TYPE_AUTOCOMPLETE
                || this.inputType === INPUT_TYPE_SELECT_CHOSEN
                || this.inputType === INPUT_MULTISELECT;
        },
    },
    async mounted() {
        await this.load();

        if (this.isCheckbox) {
            this.onCheckboxInput();
        }
    },
    methods: {
        onCheckboxInput() {
            this.$emit('update-value', { ruleFieldId: this.fieldId, value: this.selectedInternalFormCheckbox });
        },
        onDatePickerInput() {
            const formattedDate = convertFromDpToString(this.selectedDatePicker);

            this.$emit('update-value', { ruleFieldId: this.fieldId, value: formattedDate });
        },
        onBlurSaveInputTextField() {
            this.$emit('update-value', { ruleFieldId: this.fieldId, value: this.selectedInputTextField });
        },
        onMultiselectInput() {
            this.$emit('update-value', { ruleFieldId: this.fieldId, value: this.selectedMultiselectInputField.value });
        },
        onAutoCompleteInput() {
            this.$emit('update-value', { ruleFieldId: this.fieldId, value: this.selectedInputAutocomplete.map((item) => item.value) });
        },
        toggleRadioOption() {
            this.$emit('update-value', { ruleFieldId: this.fieldId, value: this.selectedRadio });
        },
        toggleYesNoRadio() {
            this.$emit('update-value', { ruleFieldId: this.fieldId, value: this.radioYesNoValue });
        },
        async load() {
            const payloadTestInputType = {
                fieldKey: this.fieldKey,
                ...this.ddKeys,
            };

            const payloadTestInputAttributes = {
                fieldId: this.fieldId,
                fieldValue: null,
                fieldKey: this.fieldKey,
                ...this.ddKeys,
            };

            const payloadLabel = {
                fieldKey: this.fieldKey,
                ...this.ddKeys,
            };

            this.inputType = await getTestInputType(payloadTestInputType);
            const { map } = await getTestInputAttributes(payloadTestInputAttributes);

            const {
                selectedValue,
                items = {},
                objectType,
                firstOptions,
                drillDownOptions,
                multiple = false,
            } = map;

            if (multiple) {
                this.isMultipleInput = true;
            }

            if (Object.keys(items).length > 0) {
                const { selectOptions } = items;

                if (selectOptions) {
                    this.multiselectInputFieldOptions = Object.entries(selectOptions).map(([value, label]) => ({
                        value,
                        label,
                    }));
                } else if (multiple) {
                    this.autocompleteOptions = Object.entries(items).map(([value, label]) => {
                        return {
                            value,
                            label,
                        };
                    });
                } else if (this.isSelectRadio) {
                    this.radioOptions = Object.entries(items).map(([value, label]) => {
                        return {
                            value,
                            label,
                        };
                    });
                } else {
                    this.multiselectInputFieldOptions = Object.entries(items).map(([value, label]) => {
                        return {
                            value,
                            label,
                        };
                    });
                }
            } else if (this.isDatePicker) {
                this.selectedDatePicker = (selectedValue === 'null' || selectedValue == null) ? '' : convertFromStringToDp(selectedValue);
            } else if (this.isYesNoRadio) {
                this.radioYesNoValue = (selectedValue === 'null' || selectedValue == null) ? '' : selectedValue;
            } else if (this.isInputTextField) {
                this.selectedInputTextField = (selectedValue === 'null' || selectedValue == null) ? '' : selectedValue;
            } else if (this.isMultiselectInputField) {
                if (objectType === 'drilldownOption') {
                    this.multiselectInputFieldOptions = Object.keys(firstOptions).map((key) => {
                        const options = Object.keys(drillDownOptions[key]).map((optionKey) => ({
                            value: optionKey,
                            label: drillDownOptions[key][optionKey],
                        }));

                        return {
                            label: firstOptions[key],
                            options,
                        };
                    });
                } else if (objectType === 'user') {
                    const response = await getAllUsers();

                    if (response && response.map) {
                        const { map: userMap } = await getAllUsers();

                        this.multiselectInputFieldOptions = Object.entries(userMap).map(([value, label]) => ({
                            value,
                            label,
                        }));
                    } else {
                        this.multiselectInputFieldOptions = [];
                    }
                } else if (selectedValue !== null || selectedValue !== 'null') {
                    this.selectedMultiselectInputField = this.multiselectInputFieldOptions.find((v) => {
                        return v.value === selectedValue;
                    });
                }
            }

            this.label = await getLabel(payloadLabel);
        },
    },
};
</script>

<style scoped lang="scss">
.test-decision-element {
    margin-bottom: $spacing-050;
}
.test-element .ds-datepicker-wrapper {
    max-width: 50%;
}

.test-element {
    width: 100%;
}
</style>
<style lang="scss">
.test-decision-element {
    .test-element {
        .date-picker-container label {
            padding-top: 0px;
        }
    }
}
</style>
